import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="manual-purchase"
// Uses event.detail.value from hw-combobox:selection events
// and queries hidden inputs by name for initial/current values.
export default class extends Controller {
  static targets = [
    "manualDiscountInput",
    "originalPrice",
    "finalPrice",
    "memberDiscountRow",
    "memberDiscountPercent",
    "memberDiscountAmount",
    "paymentDiscountRow",
    "paymentDiscountPercent",
    "paymentDiscountAmount",
    "manualDiscountRow",
    "manualDiscountDisplayPercent",
    "manualDiscountDisplayAmount",
    "freeMessage"
  ]

  basePrice = 0
  calculatedFinalPrice = 0

  connect() {
    // Store selected values (combobox values are in hidden fields)
    this.selectedMemberId = null
    this.selectedPlanId = null
    this.selectedPaymentMethod = null

    // Read pre-selected values from hidden inputs after combobox initializes
    setTimeout(() => {
      const memberInput = this.element.querySelector('input[name="member_id"]')
      const planInput = this.element.querySelector('input[name="membership_plan_id"]')
      const paymentInput = this.element.querySelector('input[name="payment_method"]')

      if (memberInput?.value) this.selectedMemberId = memberInput.value
      if (planInput?.value) this.selectedPlanId = planInput.value
      if (paymentInput?.value) this.selectedPaymentMethod = paymentInput.value

      if (this.selectedMemberId && this.selectedPlanId) {
        this.calculatePrice()
      }
    }, 200)
  }

  // Called when member combobox selection changes
  memberChanged(event) {
    this.selectedMemberId = event?.detail?.value || null
    this.calculatePrice()
  }

  // Called when plan combobox selection changes
  planChanged(event) {
    this.selectedPlanId = event?.detail?.value || null
    this.calculatePrice()
  }

  // Called when payment method combobox selection changes
  paymentMethodChanged(event) {
    this.selectedPaymentMethod = event?.detail?.value || null
    this.calculatePrice()
  }

  // Called when manual discount input changes
  discountChanged() {
    this.calculatePrice()
  }

  calculatePrice() {
    const memberId = this.selectedMemberId || ''
    const planId = this.selectedPlanId || ''
    const paymentMethod = this.selectedPaymentMethod || ''
    const manualDiscount = this.hasManualDiscountInputTarget ? parseFloat(this.manualDiscountInputTarget.value) || 0 : 0

    if (!memberId || !planId) {
      this.resetDisplay()
      return
    }

    const url = `/purchases/calculate_price?member_id=${memberId}&membership_plan_id=${planId}&payment_method=${paymentMethod}`

    fetch(url)
      .then(response => response.json())
      .then(data => {
        this.basePrice = parseFloat(data.original_price) || 0
        this.calculatedFinalPrice = parseFloat(data.final_price) || 0

        if (this.hasOriginalPriceTarget) {
          this.originalPriceTarget.textContent = '$' + this.basePrice.toFixed(2)
        }

        // Member discount
        this.updateMemberDiscount(data)

        // Payment method discount
        this.updatePaymentDiscount(data)

        // Apply and show manual discount on top
        const finalPrice = this.applyManualDiscount(manualDiscount)

        if (this.hasFinalPriceTarget) {
          this.finalPriceTarget.textContent = '$' + Math.max(0, finalPrice).toFixed(2)
        }

        // Free membership message
        if (this.hasFreeMessageTarget) {
          this.freeMessageTarget.style.display = (data.free || finalPrice <= 0) ? 'block' : 'none'
        }
      })
      .catch(error => {
        console.error('Error calculating price:', error)
      })
  }

  updateMemberDiscount(data) {
    if (!this.hasMemberDiscountRowTarget) return

    if (data.breakdown && data.breakdown.member_discount && data.breakdown.member_discount.percent > 0) {
      this.memberDiscountRowTarget.style.display = 'flex'
      if (this.hasMemberDiscountPercentTarget) {
        this.memberDiscountPercentTarget.textContent = data.breakdown.member_discount.percent
      }
      if (this.hasMemberDiscountAmountTarget) {
        this.memberDiscountAmountTarget.textContent = '-$' + parseFloat(data.breakdown.member_discount.amount).toFixed(2)
      }
    } else {
      this.memberDiscountRowTarget.style.display = 'none'
    }
  }

  updatePaymentDiscount(data) {
    if (!this.hasPaymentDiscountRowTarget) return

    if (data.breakdown && data.breakdown.payment_method_discount && data.breakdown.payment_method_discount.percent > 0) {
      this.paymentDiscountRowTarget.style.display = 'flex'
      if (this.hasPaymentDiscountPercentTarget) {
        this.paymentDiscountPercentTarget.textContent = data.breakdown.payment_method_discount.percent
      }
      if (this.hasPaymentDiscountAmountTarget) {
        this.paymentDiscountAmountTarget.textContent = '-$' + parseFloat(data.breakdown.payment_method_discount.amount).toFixed(2)
      }
    } else {
      this.paymentDiscountRowTarget.style.display = 'none'
    }
  }

  applyManualDiscount(manualDiscount) {
    let finalPrice = this.calculatedFinalPrice

    if (!this.hasManualDiscountRowTarget) return finalPrice

    if (manualDiscount > 0) {
      const manualDiscountAmt = this.calculatedFinalPrice * (manualDiscount / 100)
      finalPrice = this.calculatedFinalPrice - manualDiscountAmt
      this.manualDiscountRowTarget.style.display = 'flex'
      if (this.hasManualDiscountDisplayPercentTarget) {
        this.manualDiscountDisplayPercentTarget.textContent = manualDiscount
      }
      if (this.hasManualDiscountDisplayAmountTarget) {
        this.manualDiscountDisplayAmountTarget.textContent = '-$' + manualDiscountAmt.toFixed(2)
      }
    } else {
      this.manualDiscountRowTarget.style.display = 'none'
    }

    return finalPrice
  }

  resetDisplay() {
    if (this.hasOriginalPriceTarget) this.originalPriceTarget.textContent = '$0.00'
    if (this.hasFinalPriceTarget) this.finalPriceTarget.textContent = '$0.00'
    if (this.hasMemberDiscountRowTarget) this.memberDiscountRowTarget.style.display = 'none'
    if (this.hasPaymentDiscountRowTarget) this.paymentDiscountRowTarget.style.display = 'none'
    if (this.hasManualDiscountRowTarget) this.manualDiscountRowTarget.style.display = 'none'
    if (this.hasFreeMessageTarget) this.freeMessageTarget.style.display = 'none'
  }
}
